import { useState } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import { DataTable } from '../../../components/ui/data-table';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Plus, Filter, Users, Calendar, Target } from 'lucide-react';
import { columns } from './program-columns';
import { usePrograms } from '../hooks/usePrograms';
import { ProgramFilters } from './ProgramFilters';
import { NewProgramDialog } from './NewProgramDialog';
import { EditProgramDialog } from './EditProgramDialog';
import { ProgramDetailsDialog } from './ProgramDetailsDialog';
import { ProgramReportDialog } from './ProgramReportDialog';
import { OutreachProgram } from '../types/outreach';
import { StatCard } from '../../../components/dashboard/stat-card';

export function OutreachPrograms() {
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showNewProgram, setShowNewProgram] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<OutreachProgram | null>(null);
  const [showEditProgram, setShowEditProgram] = useState(false);
  const [showProgramDetails, setShowProgramDetails] = useState(false);
  const [showProgramReport, setShowProgramReport] = useState(false);
  
  const debouncedSearch = useDebounce(search, 300);

  const { data: programs, isLoading } = usePrograms({
    search: debouncedSearch,
  });

  const activePrograms = programs?.filter(p => p.status === 'active') || [];
  const plannedPrograms = programs?.filter(p => p.status === 'planned') || [];
  const totalParticipants = programs?.reduce((acc, p) => acc + (p.actualParticipants || 0), 0) || 0;
  const totalPatientsServed = programs?.reduce((acc, p) => acc + (p.metrics?.patientsServed || 0), 0) || 0;

  const handleProgramAction = (action: string, program: OutreachProgram) => {
    setSelectedProgram(program);
    switch (action) {
      case 'view':
        setShowProgramDetails(true);
        break;
      case 'edit':
        setShowEditProgram(true);
        break;
      case 'report':
        setShowProgramReport(true);
        break;
      case 'start':
        // TODO: Implement start program functionality
        console.log('Start program:', program.id);
        break;
      case 'complete':
        // TODO: Implement complete program functionality
        console.log('Complete program:', program.id);
        break;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-foreground">Outreach Programs</h1>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowFilters(true)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
            <Button 
              size="sm"
              onClick={() => setShowNewProgram(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Program
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <StatCard
            title="Active Programs"
            value={activePrograms.length.toString()}
            subtitle="Currently running"
            icon={Target}
            variant="primary"
            isLoading={isLoading}
          />
          
          <StatCard
            title="Planned Programs"
            value={plannedPrograms.length.toString()}
            subtitle="Upcoming programs"
            icon={Calendar}
            variant="warning"
            isLoading={isLoading}
          />

          <StatCard
            title="Total Participants"
            value={totalParticipants.toString()}
            subtitle="Across all programs"
            icon={Users}
            variant="success"
            isLoading={isLoading}
          />

          <StatCard
            title="Patients Served"
            value={totalPatientsServed.toString()}
            subtitle="Healthcare impact"
            icon={Users}
            variant="primary"
            isLoading={isLoading}
          />
        </div>
      </div>

      <div className="bg-card rounded-lg border shadow-sm">
        <div className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center gap-4">
              <Input
                placeholder="Search programs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
            </div>

            <DataTable
              columns={columns(handleProgramAction)}
              data={programs || []}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>

      <ProgramFilters
        open={showFilters}
        onOpenChange={setShowFilters}
      />

      <NewProgramDialog
        open={showNewProgram}
        onOpenChange={setShowNewProgram}
      />

      {selectedProgram && (
        <>
          <EditProgramDialog
            open={showEditProgram}
            onOpenChange={setShowEditProgram}
            program={selectedProgram}
          />

          <ProgramDetailsDialog
            open={showProgramDetails}
            onOpenChange={setShowProgramDetails}
            program={selectedProgram}
          />

          <ProgramReportDialog
            open={showProgramReport}
            onOpenChange={setShowProgramReport}
            program={selectedProgram}
          />
        </>
      )}
    </div>
  );
}
